import { useCallback, useMemo, useState } from "react";
import { type Components, Virtuoso } from "react-virtuoso";

import type { WhatsNewItemResponse } from "@btnet/services-btnet/handlers/models/whatsNewItemResponse";

import { WhatsNewCard } from "entity/whatsNew/panel/WhatsNewCard";
import { WhatsNewCardSkeleton } from "entity/whatsNew/panel/WhatsNewCardSkeleton";

import "./WhatsNewCardList.less";

/** Tallest the list grows before it scrolls. Most of the time there are only a few announcements. */
const maxListHeight = 600;

/** Padding, not margin - margins collapse and Virtuoso measures wrong. */
const itemWrapperClassName = "p-3";

interface IWhatsNewVirtuosoContext {
    hasMore: boolean;
    isLoadingMore: boolean;
}

function WhatsNewListVirtuosoFooter({ context }: Readonly<{ context?: IWhatsNewVirtuosoContext }>) {
    if (!context?.hasMore || !context.isLoadingMore) {
        return null;
    }

    return (
        <>
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
        </>
    );
}

const virtuosoComponents: Components<WhatsNewItemResponse, IWhatsNewVirtuosoContext> = {
    Footer: WhatsNewListVirtuosoFooter,
};

interface IWhatsNewCardListProps {
    items: WhatsNewItemResponse[];
    hasMore: boolean;
    isLoadingMore: boolean;
    emptyStateComponent: React.ReactNode;
    isUnread: (item: WhatsNewItemResponse) => boolean;

    onLoadMore: () => void;
    onCtaClicked: (item: WhatsNewItemResponse) => void;
    onVideoClicked: (item: WhatsNewItemResponse) => void;
}

export function WhatsNewCardList({
    items,
    hasMore,
    isLoadingMore,
    emptyStateComponent,
    isUnread,
    onLoadMore,
    onCtaClicked,
    onVideoClicked,
}: Readonly<IWhatsNewCardListProps>) {
    const context: IWhatsNewVirtuosoContext = useMemo(
        () => ({ hasMore, isLoadingMore }),
        [hasMore, isLoadingMore],
    );

    const [contentHeight, setContentHeight] = useState(0);
    const listHeight = Math.min(contentHeight || maxListHeight, maxListHeight);

    // Typed explicitly: VirtuosoProps["itemContent"] loses its generics here and both params
    // land as `any`.
    const renderCard = useCallback(
        (index: number, item: WhatsNewItemResponse) => (
            <>
                <WhatsNewCard
                    key={item.id}
                    className={itemWrapperClassName}
                    item={item}
                    isUnread={isUnread(item)}
                    onCtaClicked={onCtaClicked}
                    onVideoClicked={onVideoClicked}
                />
                {/* Starts under the card text, past the dot column (dot + gap = 18px). */}
                {index < items.length - 1 && (
                    <div className="pr-3 pl-7.5">
                        <div className="border-b border-default" />
                    </div>
                )}
            </>
        ),
        [items.length, isUnread, onCtaClicked, onVideoClicked],
    );

    return (
        // No height or overflow here - Virtuoso sets its own, and a rule here would fight it.
        <div className="WhatsNewCardList flex flex-col">
            {items.length === 0 && emptyStateComponent}
            {items.length !== 0 && (
                <Virtuoso
                    className="VirtuosoOverflowAnchorFix"
                    data={items}
                    // Undefined while a page is loading - guarding inside the handler instead
                    // lets Virtuoso queue duplicate page loads.
                    endReached={hasMore && !isLoadingMore ? onLoadMore : undefined}
                    components={virtuosoComponents}
                    increaseViewportBy={400}
                    context={context}
                    // Virtuoso needs a sized parent; clamp its reported height instead.
                    totalListHeightChanged={setContentHeight}
                    style={{ overflowX: "hidden", height: listHeight }}
                    itemContent={renderCard}
                />
            )}
        </div>
    );
}

export function WhatsNewCardListSkeleton() {
    return (
        <div className="flex flex-col">
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
            <WhatsNewCardSkeleton className={itemWrapperClassName} />
        </div>
    );
}
